import { useCallback, useEffect, useState, type FormEvent } from "react";
import { CheckCircle2, ShieldCheck } from "lucide-react";

import {
  deleteEmailSender,
  fetchEmailSender,
  saveEmailSender,
  type EmailSenderScope,
} from "@/api/emailSettings";
import { useToast } from "@/components/ToastProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type FormState = {
  email_address: string;
  from_name: string;
  reply_to: string;
  smtp_host: string;
  smtp_port: string;
  smtp_security: "starttls" | "tls";
  smtp_username: string;
  password: string;
};

const EMPTY_FORM: FormState = {
  email_address: "",
  from_name: "",
  reply_to: "",
  smtp_host: "",
  smtp_port: "587",
  smtp_security: "starttls",
  smtp_username: "",
  password: "",
};

export function SmtpSenderSettings({ scope, artistName }: { scope: EmailSenderScope; artistName?: string | null }) {
  const toast = useToast();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [configured, setConfigured] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState("not_configured");
  const [lastVerifiedAt, setLastVerifiedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const account = await fetchEmailSender(scope);
      setConfigured(account.configured);
      setVerificationStatus(account.verification_status);
      setLastVerifiedAt(account.last_verified_at);
      setForm({
        email_address: account.email_address ?? "",
        from_name: account.from_name ?? "",
        reply_to: account.reply_to ?? "",
        smtp_host: account.smtp_host ?? "",
        smtp_port: String(account.smtp_port ?? 587),
        smtp_security: account.smtp_security ?? "starttls",
        smtp_username: account.smtp_username ?? "",
        password: "",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load email sender settings.");
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    void load();
  }, [load, artistName]);

  const update = (field: keyof FormState, value: string) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const account = await saveEmailSender(scope, {
        email_address: form.email_address.trim(),
        from_name: form.from_name.trim() || null,
        reply_to: form.reply_to.trim() || null,
        smtp_host: form.smtp_host.trim(),
        smtp_port: Number(form.smtp_port),
        smtp_security: form.smtp_security,
        smtp_username: form.smtp_username.trim(),
        ...(form.password ? { password: form.password } : {}),
        enabled: true,
      });
      setConfigured(true);
      setVerificationStatus(account.verification_status);
      setLastVerifiedAt(account.last_verified_at);
      setForm((current) => ({ ...current, password: "" }));
      toast.push({ title: "Email sender", message: "SMTP credentials verified and saved." });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to verify SMTP credentials.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(scope === "artist" ? "Remove this artist override and use your default sender?" : "Disconnect your default email sender?")) return;
    setSaving(true);
    setError(null);
    try {
      await deleteEmailSender(scope);
      setConfigured(false);
      setVerificationStatus("not_configured");
      setLastVerifiedAt(null);
      setForm(EMPTY_FORM);
      toast.push({ title: "Email sender", message: scope === "artist" ? "Artist override removed." : "Email sender disconnected." });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to disconnect email sender.");
    } finally {
      setSaving(false);
    }
  };

  const connected = configured && verificationStatus === "connected";
  const verifiedLabel = lastVerifiedAt
    ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(lastVerifiedAt))
    : null;

  return (
    <section className="aa-glass-card space-y-5 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            {scope === "artist" ? "Artist email sender" : "Default email sender"}
          </h2>
          <p className="text-xs text-muted-foreground">
            {scope === "artist"
              ? `Optional override for ${artistName || "the active artist"}. Remove it to use your account default.`
              : "Used for contact outreach unless the active artist has its own override."}
          </p>
        </div>
        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${connected ? "border-emerald-400/50 bg-emerald-500/10 text-emerald-200" : "border-border text-muted-foreground"}`}>
          {connected ? "Connected" : configured ? "Needs verification" : "Not connected"}
        </span>
      </div>
      {loading ? <p className="text-sm text-muted-foreground">Loading sender settings…</p> : (
        <form className="space-y-4" onSubmit={submit}>
          {connected && (
            <div className="rounded-xl border border-emerald-400/35 bg-emerald-500/10 p-4" role="status" aria-live="polite">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-emerald-100">Connected and ready to send</p>
                  <p className="mt-1 break-all text-sm text-foreground">{form.email_address}</p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>{scope === "artist" ? `${artistName || "Active artist"} override` : "Account default sender"}</span>
                    <span>{form.smtp_host}:{form.smtp_port} · {form.smtp_security === "tls" ? "TLS/SSL" : "STARTTLS"}</span>
                    {verifiedLabel && <span>Verified {verifiedLabel}</span>}
                  </div>
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-200/80">
                    <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
                    Credentials are encrypted and the saved password is never displayed.
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>From email</Label><Input type="email" required value={form.email_address} onChange={(event) => update("email_address", event.target.value)} /></div>
            <div><Label>From name</Label><Input value={form.from_name} onChange={(event) => update("from_name", event.target.value)} /></div>
            <div><Label>Reply-to (optional)</Label><Input type="email" value={form.reply_to} onChange={(event) => update("reply_to", event.target.value)} /></div>
            <div><Label>SMTP username</Label><Input required value={form.smtp_username} onChange={(event) => update("smtp_username", event.target.value)} autoComplete="username" /></div>
            <div><Label>SMTP host</Label><Input required placeholder="smtp.example.com" value={form.smtp_host} onChange={(event) => update("smtp_host", event.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Port</Label><Input type="number" min="1" max="65535" required value={form.smtp_port} onChange={(event) => update("smtp_port", event.target.value)} /></div>
              <div><Label>Security</Label><select className="mt-1 min-h-[40px] w-full rounded-md border border-border bg-background/40 px-3 text-sm" value={form.smtp_security} onChange={(event) => update("smtp_security", event.target.value)}><option value="starttls">STARTTLS</option><option value="tls">TLS/SSL</option></select></div>
            </div>
          </div>
          <div><Label>{configured ? "New SMTP password (leave blank to keep current)" : "SMTP password"}</Label><Input type="password" required={!configured} value={form.password} onChange={(event) => update("password", event.target.value)} autoComplete="new-password" /></div>
          <p className="text-xs text-muted-foreground">The password is encrypted at rest and is never returned to the browser. Saving verifies the connection.</p>
          {error && <p className="text-xs text-rose-400" role="alert">{error}</p>}
          <div className="flex flex-wrap justify-end gap-2">
            {configured && <Button type="button" variant="outline" disabled={saving} onClick={() => void remove()}>Disconnect</Button>}
            <Button type="submit" disabled={saving}>{saving ? "Verifying…" : "Verify and save"}</Button>
          </div>
        </form>
      )}
    </section>
  );
}
